import React, { useLayoutEffect, useRef } from 'react'
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import { localizationAMChart } from '../../utils/AMChartsSettings'
import { useTranslation } from 'react-i18next'

function PersonnelClusteredBarChart ({ response, code, chartTitle, xTitle, yTitle, chartSubTitle, yearlyHsSums, periodInMonths }) {
  const rootRef = useRef(null)
  const { t } = useTranslation()

  useLayoutEffect(() => {
    const root = am5.Root.new('clusteredBarChart')
    localizationAMChart(root)

    const chart = root.container.children.push(am5xy.XYChart.new(root, {
      panX: true,
      panY: false,
      wheelX: 'panX',
      wheelY: 'zoomX',
      layout: root.verticalLayout,
      exportable: true,
      paddingTop: 0,
      paddingBottom: 0
    }))

    const legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: am5.p50,
        x: am5.p50,
        marginTop: 5,
        marginBottom: 0
      })
    )

    const yAxis = chart.yAxes.push(am5xy.CategoryAxis.new(root, {
      categoryField: 'countryCode',
      renderer: am5xy.AxisRendererY.new(root, {
        cellStartLocation: 0.1,
        cellEndLocation: 0.9
      })
    }))

    yAxis.get('renderer').labels.template.setAll({
      oversizedBehavior: 'wrap',
      maxWidth: 90,
      textAlign: 'right'
    })

    response?.stackedData && yAxis.data.setAll(response?.stackedData)

    const xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, {
      extraMax: 0.3,
      renderer: am5xy.AxisRendererX.new(root, {})
    }))

    // Format date range from yearlyHsSums key
    const formatDateRange = (key) => {
      if (!key || !periodInMonths) return key

      const currentLocale = t('locale') === 'tr' ? 'tr' : 'en'
      const year = parseInt(key.substring(0, 4))
      const month = parseInt(key.substring(4, 6))

      // Both periods: key is end date, go backward periodInMonths
      // Example: 202301 with 6 months -> Jul 2022 - Jan 2023
      // Example: 202401 with 6 months -> Jul 2023 - Jan 2024
      const endDate = new Date(year, month - 1, 1)
      const startDate = new Date(year, month - 1, 1)
      startDate.setMonth(startDate.getMonth() - periodInMonths)

      const formatDate = (date) => {
        return date.toLocaleDateString(currentLocale === 'tr' ? 'tr-TR' : 'en-US', {
          month: 'long',
          year: 'numeric'
        })
      }

      return `${formatDate(startDate)} - ${formatDate(endDate)}`
    }

    function createSeries (field, txtField, tooltipField, name, data) {
      const series = chart.series.push(am5xy.ColumnSeries.new(root, {
        name,
        xAxis,
        yAxis,
        valueXField: field,
        categoryYField: 'countryCode',
        sequencedInterpolation: true
      }))

      series.columns.template.setAll({
        tooltipText: `{${tooltipField}}`
      })
      series.columns.template.setAll({
        height: response?.stackedData?.length > 3 ? am5.percent(80) : am5.percent(40)
      })
      series.bullets.push(function () {
        return am5.Bullet.new(root, {
          locationX: 1,
          locationY: 0.5,
          sprite: am5.Label.new(root, {
            centerY: am5.p50,
            text: `{${txtField}}`,
            populateText: true
          })
        })
      })

      series.data.setAll(data || response?.stackedData)

      legend.data.push(series)

      series.appear()

      return series
    }

    const getDateLabels = () => {
      if (!yearlyHsSums) {
        return {
          first: response?.stackedData[0]?.previous12MonthsName || '',
          second: response?.stackedData[0]?.last12MonthsName || ''
        }
      }

      const keys = Object.keys(yearlyHsSums)
      if (keys.length < 2) {
        return {
          first: response?.stackedData[0]?.previous12MonthsName || '',
          second: response?.stackedData[0]?.last12MonthsName || ''
        }
      }

      return {
        first: formatDateRange(keys[0]),
        second: formatDateRange(keys[1])
      }
    }

    const { first, second } = getDateLabels()

    const enrichedData = response?.stackedData?.map(item => ({
      ...item,
      previous12MonthsTooltip: `${first}: ${item.previous12MonthsTxt}`,
      last12monthsTooltip: `${second}: ${item.last12MonthsTxt}`
    }))

    if (enrichedData) {
      yAxis.data.setAll(enrichedData)
      createSeries('previous12Months', 'previous12MonthsTxt', 'previous12MonthsTooltip', first, enrichedData)
      createSeries('last12Months', 'last12MonthsTxt', 'last12monthsTooltip', second, enrichedData)
    }
    rootRef.current = root

    chart.appear()

    return () => {
      root.dispose()
    }
  }, [response, yearlyHsSums, periodInMonths, t])

  return <div id='clusteredBarChart' style={{ width: '100%', height: '100%', minHeight: '500px' }}></div>
}

export default PersonnelClusteredBarChart
